'use client';

/** @jsxRuntime classic */
/** @jsx jsx */

import { jsx } from '@emotion/react';
import Button from '@egalite/ui/Buttons';
import ErrorList from '@egalite/ui/ErrorList';
import { InputField } from '@egalite/ui/Form';
import SelectField from '@egalite/ui/Form/SelectField';
import { useNotification } from '@egalite/ui/IncluiPCD/NotificationProvider';
import Loading from '@egalite/ui/Loading';
import Typography from '@egalite/ui/Typography';
import Cookies from 'js-cookie';
import { useRouter, useSearchParams } from 'next/navigation';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import authFetch from '../../lib/auth/authFetch';
import useForm from '../../lib/hooks/useForm';
import validationSchema, { initialValues } from '../../lib/validation/candidate/professionalDataEducation';
import ButtonsPages from './legacy_blocks/ButtonsPages';
import FormPage from './legacy_blocks/CandidateFormPage';
import save from './save';
import useSomeChangedEffect from './useSomeChangedEffect';

const degrees = [
	{ value: 'Ensino Fundamental', label: 'Ensino Fundamental' },
	{ value: 'Ensino Médio', label: 'Ensino Médio' },
	{ value: 'Ensino Técnico', label: 'Ensino Técnico' },
	{ value: 'Ensino Superior', label: 'Ensino Superior' },
	{ value: 'Pós-Graduação', label: 'Pós-Graduação' },
	{ value: 'Mestrado', label: 'Mestrado' },
	{ value: 'Doutorado', label: 'Doutorado' },
];

const sectors = [
	{ value: 'Administrativo', label: 'Administrativo' },
	{ value: 'Atendimento', label: 'Atendimento' },
	{ value: 'Auditoria', label: 'Auditoria' },
	{ value: 'Comercial', label: 'Comercial' },
	{ value: 'Compliance', label: 'Compliance' },
	{ value: 'Compras', label: 'Compras' },
	{ value: 'Construção', label: 'Construção' },
	{ value: 'Fiscal', label: 'Fiscal' },
	{ value: 'Alimentação', label: 'Alimentação' },
	{ value: 'Educação', label: 'Educação' },
	{ value: 'Elétrica/eletrônica', label: 'Elétrica/eletrônica' },
	{ value: 'Engenharia', label: 'Engenharia' },
	{ value: 'Financeiro', label: 'Financeiro' },
	{ value: 'Indústria', label: 'Indústria' },
	{ value: 'Jurídico', label: 'Jurídico' },
	{ value: 'Logística', label: 'Logística' },
	{ value: 'Manutenção', label: 'Manutenção' },
	{ value: 'Marketing', label: 'Marketing' },
	{ value: 'Qualidade', label: 'Qualidade' },
	{ value: 'Recursos Humanos', label: 'Recursos Humanos' },
	{ value: 'Saúde', label: 'Saúde' },
	{ value: 'Segurança do trabalho', label: 'Segurança do trabalho' },
	{ value: 'Tecnologia (TI)', label: 'Tecnologia (TI)' },
	{ value: 'Outro', label: 'Outro' },
].sort((a, b) => (a.label > b.label ? 1 : -1));

const ProfessionalDataEducationForm: FC<{ edit?: boolean; onSuccess?: () => void }> = ({ edit = false, onSuccess = null }) => {
	const notification = useNotification();
	const [loadingData, setLoadingData] = useState(true);
	const [candidateData, setCandidateData] = useState(null);
	const [educationData, setEducationData] = useState(initialValues);
	const searchParams = useSearchParams();

	const router = useRouter();
	const [coursesList, setCoursesList] = useState([]);
	const courseSelect = useRef<{ setSelectedValue: (string) => void }>();
	const courseDescriptionSelect = useRef<{ setSelectedValue: (string) => void }>();

	const [formController, getFieldProps, { loading, submitting, success, errors, data }] = useForm({
		initialValues: educationData,
		validationSchema,
		mutation: data => save('/api/v1/candidate/professionalDataEducation', data),
		onSuccess:
			onSuccess != null
				? () => onSuccess()
				: () => {
						router.push(`/candidato/form/professionalDataEducationIndex`, { scroll: true });
				  },
	});

	useSomeChangedEffect(
		() => {
			const selectedDegree = formController.values?.degree;
			if (educationData && selectedDegree != educationData.degree) {
				if (courseSelect.current && courseSelect.current.setSelectedValue) {
					courseSelect.current?.setSelectedValue('');
				}
				if (courseDescriptionSelect.current && courseDescriptionSelect.current.setSelectedValue) {
					courseDescriptionSelect.current?.setSelectedValue('');
				}
				formController.setFieldValue('course', '');
				formController.setFieldValue('courseDescription', '');
			}

			if (['Ensino Técnico', 'Ensino Superior'].includes(selectedDegree)) {
				fetch(`/api/v1/courses-list${selectedDegree === 'Ensino Técnico' ? `?list=tecnico` : ''}`)
					.then(res => res.json())
					.then(res => {
						if (res.success) {
							setCoursesList((res.data || []).map(sector => ({ value: sector, label: sector })));
						}
					})
					.catch(error => {
						console.error(error);
					});
			}
		},
		[candidateData, searchParams, educationData, formController],
		[formController.values?.degree],
	);

	useEffect(() => {
		if (loadingData) {
			authFetch('/api/v2/candidate/profile', Cookies.get('token'))
				.then(res => res.json())
				.then(data => {
					setLoadingData(false);
					if (data.success === true) {
						setCandidateData(data.data);
						const n = searchParams.get('n');
						if (n == null) {
							// setEducationData(initialValues);
						} else if (data.data.education && data.data.education[n] != null) {
							const educationData = data.data.education[n];
							const newValues = Object.keys(initialValues).reduce((acc, key) => {
								acc[key] = educationData[key] ?? initialValues[key];
								return acc;
							}, {});
							(newValues as typeof initialValues).id = n;
							setEducationData(newValues as typeof initialValues);
						} else {
							notification.error('Erro ao carregar dados do candidato');
						}
					} else {
						notification.error('Erro ao carregar dados do candidato');
					}
				});
		}
	}, [formController, router, loadingData, notification, searchParams]);

	if (loadingData) {
		return <Loading fullScreen={true}>carregando dados</Loading>;
	}

	return (
		<FormPage
			actualStep={3}
			formActualStep={3}
			formTotalSteps={5}
			progress={60}
			formTitle={'Formação acadêmica'}
			// previousForm="/"
			showStepsMenu={false}
			// showBackButton={false}
			modal={edit}
		>
			<form onSubmit={formController.handleSubmit}>
				{edit ? null : (
					<Typography variant="headline-5" className="text-brand-main mb-16">
						{'Formação acadêmica'}
					</Typography>
				)}

				<SelectField
					id="degree"
					label={'Formação Acadêmica'}
					placeholder={'Selecione...'}
					options={degrees}
					{...getFieldProps('degree')}
					className='my-12'
				/>
				<SelectField
					id="status"
					label={'Situação'}
					placeholder={'Selecione...'}
					options={[
						{ value: 'Completo', label: 'Completo' },
						{ value: 'Cursando', label: 'Cursando' },
						{ value: 'Incompleto', label: 'Incompleto' },
						{ value: 'Trancado', label: 'Trancado' },
					]}
					{...getFieldProps('status')}
					className='my-12'
				/>

				{['', 'Ensino Fundamental', 'Ensino Médio'].includes(formController.values.degree) ? null : (
					<Fragment>
						{['Pós-Graduação', 'Mestrado', 'Doutorado'].includes(formController.values.degree) ? (
							<Fragment>
								<InputField id="course" type="text" label="Título do curso" {...getFieldProps('course')} />
								<SelectField
									id="courseDescription"
									ref={courseDescriptionSelect}
									placeholder="Selecione..."
									label="Área do curso"
									options={sectors ?? []}
									{...getFieldProps('courseDescription')}
								/>
							</Fragment>
						) : (
							<Fragment>
								<SelectField
									id="course"
									ref={courseSelect}
									placeholder="Selecione..."
									label="Curso"
									options={coursesList ?? []}
									{...getFieldProps('course')}
								/>
								{['Outro'].includes(formController.values.course) ? (
									<InputField
										id="courseDescription"
										type="text"
										label="Título do curso"
										{...getFieldProps('courseDescription')}
									/>
								) : null}
							</Fragment>
						)}
						<InputField id="institution" type="text" label={'Instituição'} {...getFieldProps('institution')} />
						<InputField id="startYear" label={'Ano de início'} {...getFieldProps('startYear')} />
						{['Completo'].includes(formController.values.status) ? (
							<InputField id="endYear" label={'Ano de conclusão'} {...getFieldProps('endYear')} />
						) : null}
					</Fragment>
				)}

				<ErrorList errors={formController.errors} touched={formController.touched} />

				{errors != null &&
					errors.map((err, index) => (
						<Typography key={`err-${index}`} variant="body2" color="error.dark">
							{err}
						</Typography>
					))}
				<div className="md:mt-24 mt-16">
					{edit ? (
						<Button type="submit" variant="primary" className="w-full">
							Salvar
						</Button>
					) : (
						<ButtonsPages
							submitDisabled={submitting}
							previousForm={
								candidateData?.education?.length
									? '/candidato/form/professionalDataEducationIndex'
									: '/candidato/form/professionalData2'
							}
							hideBack={edit}
						/>
					)}
				</div>
			</form>
		</FormPage>
	);
};

export default ProfessionalDataEducationForm;
